import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './RoleSelectionPage.css';

function CombinedRoleContactPage() {
  const navigate = useNavigate();

  // Contact form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    subject: "",
    department: "general"
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  const handleSelect = (role, type) => {
    if (role === 'student') {
      navigate(type === 'login' ? '/student-login' : '/student-request');
    } else if (role === 'teacher') {
      navigate(type === 'login' ? '/teacher-login' : '/teacher-register');
    } else if (role === 'admin') {
      navigate('/admin-login');
    } else if (role === 'payment') {
      navigate('/payment');
    } else if (role === 'partner') {
      navigate('/partner-registration');
    }
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.message.trim()) errors.message = "Message is required";
    
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log("Form data:", formData);
      setIsSubmitted(true);
      setFormData({ 
        name: "", 
        email: "", 
        phone: "", 
        message: "", 
        subject: "", 
        department: "general" 
      });
      setFormErrors({});
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Department options
  const departments = [
    { value: "general", label: "General Inquiry" },
    { value: "admissions", label: "Admissions" },
    { value: "academic", label: "Academic Affairs" },
    { value: "student", label: "Student Services" },
    { value: "partnership", label: "Partnerships" },
    { value: "technical", label: "Technical Support" }
  ];

  return (
    <div className="crc-wrapper">
      {/* Hero Section - Updated */}
      <section className="crc-hero">
        <div className="crc-hero-overlay">
          <div className="crc-hero-content animate-fade-in">
            <h1>Adam International Academy Portal</h1>
            <p>Access your account, make payments, or get in touch with our team</p>
          </div>
        </div>
      </section>

      {/* Role Selection Section - Updated */}
      <section className="crc-section crc-role-section animate-slide-up">
        <div className="crc-container">
          <h2 className="crc-section-title">Select Your Account Type</h2>
          
          {/* Login Section */}
          <div className="crc-section-group">
            <h3 className="crc-subtitle">Login to Existing Account</h3>
            <div className="crc-cards">
              <div className="crc-card" onClick={() => handleSelect('student', 'login')}>
                <div className="crc-card-icon">🎓</div>
                <h4>Student Login</h4>
                <p>Access your student dashboard, view courses, and track progress</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card" onClick={() => handleSelect('teacher', 'login')}>
                <div className="crc-card-icon">👨‍🏫</div>
                <h4>Faculty Login</h4>
                <p>Access instructor resources, manage courses, and grade assignments</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card" onClick={() => handleSelect('admin', 'login')}>
                <div className="crc-card-icon">🛠️</div>
                <h4>Admin Login</h4>
                <p>System administration, user management, and reporting</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card" onClick={() => handleSelect('partner', 'login')}>
                <div className="crc-card-icon">🤝</div>
                <h4>Partner Login</h4>
                <p>Access partner portal for collaboration and resources</p>
                <div className="crc-card-arrow">→</div>
              </div>
            </div>
          </div>

          {/* Register Section */}
          <div className="crc-section-group">
            <h3 className="crc-subtitle">Create New Account</h3>
            <div className="crc-cards">
              <div className="crc-card" onClick={() => handleSelect('student', 'register')}>
                <div className="crc-card-icon">📝</div>
                <h4>Student Registration</h4>
                <p>Apply to become a student at Adam International Academy</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card" onClick={() => handleSelect('teacher', 'register')}>
                <div className="crc-card-icon">📋</div>
                <h4>Faculty Application</h4>
                <p>Apply to join our faculty of 100+ international experts</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card crc-card-disabled">
                <div className="crc-card-icon">🔒</div>
                <h4>Admin Registration</h4>
                <p>Contact us for administrative access</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card" onClick={() => handleSelect('partner', 'register')}>
                <div className="crc-card-icon">🌐</div>
                <h4>Partner Registration</h4>
                <p>Become a strategic partner with Adam Academy</p>
                <div className="crc-card-arrow">→</div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="crc-section-group">
            <h3 className="crc-subtitle">Payment & Fees</h3>
            <div className="crc-cards">
              <div className="crc-card" onClick={() => handleSelect('payment')}>
                <div className="crc-card-icon">💳</div>
                <h4>Make Payment</h4>
                <p>Pay tuition fees, course payments, or registration fees</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card crc-card-disabled">
                <div className="crc-card-icon">📄</div>
                <h4>Payment History</h4>
                <p>View your payment records and invoices</p>
                <div className="crc-card-arrow">→</div>
              </div>
              
              <div className="crc-card crc-card-disabled">
                <div className="crc-card-icon">❓</div>
                <h4>Payment Support</h4>
                <p>Get help with payment issues and refunds</p>
                <div className="crc-card-arrow">→</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information - Updated with multiple locations */}
      <section className="crc-section crc-contact-info-section animate-fade-in">
        <div className="crc-container">
          <div className="crc-contact-grid">
            <div className="crc-contact-info">
              <h2 className="crc-section-title">📞 Contact Information</h2>
              
              <div className="crc-contact-item">
                <div className="crc-contact-icon">📍</div>
                <div className="crc-contact-details">
                  <h3>Main Campus</h3>
                  <p>Istanbul, Turkey</p>
                </div>
              </div>

              <div className="crc-contact-item">
                <div className="crc-contact-icon">📍</div>
                <div className="crc-contact-details">
                  <h3>Middle East Headquarters</h3>
                  <p>Manama, Bahrain</p>
                </div>
              </div>

              <div className="crc-contact-item">
                <div className="crc-contact-icon">🌍</div>
                <div className="crc-contact-details">
                  <h3>Regional Offices</h3>
                  <p>Africa, Europe, America</p>
                </div>
              </div>
              
              <div className="crc-contact-item">
                <div className="crc-contact-icon">📞</div>
                <div className="crc-contact-details">
                  <h3>Phone</h3>
                  <p>(+90) 380-772631</p>
                  <p>(+973) 1711-2233 (Bahrain)</p>
                </div>
              </div>
              
              <div className="crc-contact-item">
                <div className="crc-contact-icon">📧</div>
                <div className="crc-contact-details">
                  <h3>Email</h3>
                  <p>info@adaminternationalacademy.com</p>
                  <p>admissions@adaminternationalacademy.com</p>
                  <p>partnerships@adaminternationalacademy.com</p>
                </div>
              </div>
              
              <div className="crc-contact-item">
                <div className="crc-contact-icon">🌐</div>
                <div className="crc-contact-details">
                  <h3>Website</h3>
                  <p>www.adaminternationalacademy.com</p>
                </div>
              </div>
              
              <div className="crc-contact-hours">
                <h3>📅 Working Hours</h3>
                <ul>
                  <li>Monday - Friday: <span>09:00 – 18:00 (Local Time)</span></li>
                  <li>Saturday: <span>10:00 – 14:00</span></li>
                  <li>Sunday: <span>Closed</span></li>
                  <li>24/7 Online Support: <span>Available</span></li>
                </ul>
              </div>
            </div>
            
            <div className="crc-map-container">
              <h2 className="crc-section-title">🗺️ Our Location</h2>
              <div className="crc-map-wrapper">
                <iframe
                  title="Adam Academy Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12045.1912!2d28.9784!3d41.0082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDEuMDA4MiwyOC45Nzg0!5e0!3m2!1str!2str!4v1692461234567"
                  width="100%"
                  height="400"
                  style={{ border: 0, borderRadius: 'var(--crc-radius)' }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
              <div className="crc-map-note">
                <p>📍 Main Campus: Istanbul, Turkey | Regional Office: Manama, Bahrain</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section - Updated with subject and department */}
      <section className="crc-section crc-contact-form-section animate-fade-in">
        <div className="crc-container">
          <h2 className="crc-section-title">✉️ Send Us a Message</h2>
          <p className="crc-form-intro">
            Have questions about admissions, programs, partnerships, or technical support? Reach out to us directly using the form below.
          </p>
          
          {isSubmitted && (
            <div className="crc-form-success">
              <div className="crc-success-icon">✓</div>
              <div className="crc-success-message">
                <h3>Your Message Has Been Sent!</h3>
                <p>We will contact you as soon as possible. Thank you for reaching out to Adam International Academy.</p>
              </div>
            </div>
          )}
          
          <form className="crc-contact-form" onSubmit={handleSubmit}>
            <div className="crc-form-row">
              <div className="crc-form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your name and surname"
                  value={formData.name}
                  onChange={handleChange}
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && <span className="crc-error">{formErrors.name}</span>}
              </div>
              
              <div className="crc-form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Your email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && <span className="crc-error">{formErrors.email}</span>}
              </div>
            </div>

            <div className="crc-form-row">
              <div className="crc-form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Your phone number (optional)"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              
              <div className="crc-form-group">
                <label htmlFor="department">Department *</label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className={formErrors.department ? 'error' : ''}
                >
                  {departments.map(dept => (
                    <option key={dept.value} value={dept.value}>
                      {dept.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="crc-form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                placeholder="Brief subject of your message"
                value={formData.subject}
                onChange={handleChange}
              />
            </div>
            
            <div className="crc-form-group">
              <label htmlFor="message">Your Message *</label>
              <textarea
                id="message"
                name="message"
                placeholder="How can we help you? Please provide as much detail as possible."
                rows="5"
                value={formData.message}
                onChange={handleChange}
                className={formErrors.message ? 'error' : ''}
              ></textarea>
              {formErrors.message && <span className="crc-error">{formErrors.message}</span>}
            </div>
            
            <div className="crc-form-note">
              <p>By submitting this form, you agree to our <a href="/terms">Terms of Use</a> and <a href="/privacy">Privacy Policy</a>.</p>
            </div>
            
            <button 
              type="submit" 
              className={`crc-btn-primary ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="crc-spinner"></span>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

export default CombinedRoleContactPage;